import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@vimaana/ui";

/**
 * Abonnierte Kalender je Kontor-Konto (Backend: hetzner/webhook/kontor_kalender_abo.py).
 *
 * Der Server ruft die ICS-Adressen ab und reicht die Termine durch — er
 * speichert keine. Jedes Konto sieht nur seine eigenen Abos.
 */

export type KalenderAbo = {
  id: number;
  name: string;
  /** Nur beim eigenen Abo gefüllt — die Adresse ist bei iCloud das Passwort des Kalenders. */
  url: string;
  farbe: string;
  aktiv: boolean;
  /** true = die Termine sehen auch die anderen Kontor-Konten. */
  geteilt: boolean;
  /** true = die Termine machen den Eigentümer im Planer „nicht da" (kontor_sperrzeit, 16.10.2026). */
  sperrt: boolean;
  /** false = von einer Kollegin oder einem Kollegen freigegeben, nur lesen. */
  eigen: boolean;
  besitzer: string;
};

export type AboTermin = {
  abo_id: number;
  kalender: string;
  /** Wem der Kalender gehört — leer beim eigenen. */
  von_wem: string;
  farbe: string;
  titel: string;
  /** Berliner Ortszeit ohne Zone, „2026-09-21T10:00"; ganztags 00:00. */
  start: string;
  /** Exklusiv — ein Ganztagstermin am 21. endet „2026-09-22T00:00". */
  ende: string;
  ganztags: boolean;
  ort: string;
  beschreibung: string;
};

export type AboTermine = { termine: AboTermin[]; fehler: { abo_id: number; kalender: string; text: string }[] };

/** Die feste Palette — muss zu FARBEN im Backend passen. */
export const ABO_FARBEN = ["#6b7fa3", "#c0673f", "#3f8f7a", "#9b4d7c", "#c49a2c", "#7a5fc0", "#5a6470", "#d0615c"];

const abosKey = ["kontor", "kalender-abos"] as const;

export function useKalenderAbos(enabled = true) {
  return useQuery<KalenderAbo[]>({
    queryKey: abosKey,
    queryFn: () => apiGet<KalenderAbo[]>("/api/kontor/kalender-abos"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Termine der aktiven Abos im Zeitraum. `enabled` = nur dort laden, wo sie gezeigt werden. */
export function useAboTermine(from: string, to: string, enabled: boolean) {
  return useQuery<AboTermine>({
    queryKey: [...abosKey, "termine", from, to],
    queryFn: () => apiGet<AboTermine>(`/api/kontor/kalender-abos/termine?from=${from}&to=${to}`),
    enabled,
    staleTime: 5 * 60_000,
  });
}

function useNeuLaden() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: abosKey });
    // Ein Abo mit „sperrt" liefert Sperrzeiten — die müssen mit.
    qc.invalidateQueries({ queryKey: ["kontor", "sperrzeiten"] });
  };
}

export function useAboAnlegen() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (body: { name: string; url: string; farbe: string }) => apiPost<KalenderAbo>("/api/kontor/kalender-abos", body),
    onSuccess: neu,
  });
}

export function useAboAendern() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: number; name?: string; farbe?: string; aktiv?: boolean; geteilt?: boolean; sperrt?: boolean }) =>
      apiPost<KalenderAbo>(`/api/kontor/kalender-abos/${id}`, felder),
    onSuccess: neu,
  });
}

export function useAboEntfernen() {
  const neu = useNeuLaden();
  return useMutation({
    mutationFn: (id: number) => apiDelete<{ entfernt: boolean }>(`/api/kontor/kalender-abos/${id}`),
    onSuccess: neu,
  });
}

/**
 * Termine nach Tag („JJJJ-MM-TT"). Mehrtägige stehen an jedem ihrer Tage;
 * ein Ende genau um Mitternacht zählt nicht mehr zum Folgetag.
 */
export function aboTermineProTag(termine: AboTermin[]): Map<string, AboTermin[]> {
  const m = new Map<string, AboTermin[]>();
  for (const t of termine) {
    const erster = t.start.slice(0, 10);
    let letzter = t.ende.slice(0, 10);
    if (t.ende > t.start && t.ende.slice(11, 16) === "00:00") {
      const d = new Date(`${letzter}T12:00:00`);
      d.setDate(d.getDate() - 1);
      letzter = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }
    if (letzter < erster) letzter = erster;
    const d = new Date(`${erster}T12:00:00`);
    for (let i = 0; i < 62; i++) {
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (k > letzter) break;
      const arr = m.get(k) ?? [];
      arr.push(t);
      m.set(k, arr);
      d.setDate(d.getDate() + 1);
    }
  }
  return m;
}
